import type { DifferencesObjectIDObject, FeatureObject } from "./newVersioningUtils";

/**
 * Reduce a full version-differences list to a single-object descriptor for the
 * given layer and objectId, preserving which bucket (insert / update / delete)
 * the object belonged to.
 *
 * Returns null if the object is not among the version's changes (wrong layer,
 * or the object id is not present in any bucket of that layer).
 *
 * objectId may arrive as a string (from preLoadJobConfig) or a number
 * (from feature attributes), so the match is found via string-coerced comparison.
 * The ORIGINAL bucket value is then preserved verbatim (the differences API
 * returns objectIds as numbers at runtime, and getObjects matches them with `===`
 * against the numeric objectIdField — coercing to a string would break that).
 */
export const filterDifferencesToObject = (
    differences: DifferencesObjectIDObject[],
    layerID: number,
    objectId: string | number
): DifferencesObjectIDObject | null => {
    const layerDiff = differences.find((d) => d.layerId === layerID);
    if (!layerDiff) return null;

    const target = String(objectId);
    const pick = (bucket?: string[]) => (bucket ?? []).find((id) => String(id) === target);

    const insMatch = pick(layerDiff.inserts);
    const updMatch = pick(layerDiff.updates);
    const delMatch = pick(layerDiff.deletes);

    if (insMatch === undefined && updMatch === undefined && delMatch === undefined) return null;

    return {
        layerId: layerID,
        inserts: insMatch !== undefined ? [insMatch] : [],
        updates: updMatch !== undefined ? [updMatch] : [],
        deletes: delMatch !== undefined ? [delMatch] : [],
    };
};

/** Foreign-key field names for a many:many relationship (junction) table layer. */
export interface RelationshipFieldSpec {
    layerId: number;
    relation1FieldName: string;
    relation2FieldName: string;
}

/**
 * From a set of loaded relationship-table (many:many junction) rows, keep only
 * those whose either foreign key references the given object's globalid.
 *
 * Why: junction rows (e.g. layer-31 Lys_IB) live in their own tables and are never
 * returned by queryRelatedFeatures, so the single-object job flow
 * (findDifferencesForObject) must load the version's changed relationship rows
 * explicitly — the same way findDifferencesForPlan does for a whole job — and feed
 * them into the relate step, otherwise the object's combined-object link is lost.
 * This scopes those rows to just the junctions that touch the clicked object, so we
 * don't drag in unrelated combined objects.
 *
 * objectGlobalId comes from the loaded object's versionGraphic; if it is missing we
 * cannot match anything and return [] (the flow then behaves as before this scoping).
 */
export const filterRelationshipRowsConnectedToObject = (
    relationshipRows: FeatureObject[],
    objectGlobalId: string | undefined,
    relationshipFieldsByLayer: Map<number, RelationshipFieldSpec>
): FeatureObject[] => {
    if (!objectGlobalId) return [];
    return relationshipRows.filter((row) => {
        const spec = relationshipFieldsByLayer.get(row.layerId);
        if (!spec) return false;
        const attrs = row.versionGraphic?.attributes ?? row.defaultGraphic?.attributes;
        if (!attrs) return false;
        return attrs[spec.relation1FieldName] === objectGlobalId || attrs[spec.relation2FieldName] === objectGlobalId;
    });
};
